<template>
  <div class="tw:sm:max-w-lg tw:md:max-w-md tw:mx-auto tw:flex tw:flex-col tw:w-full">
    <div class="tw:flex tw:flex-col tw:flex-1 tw:sm:px-0 tw:pt-16 tw:pb-4 tw:sm:pt-12 tw:md:pb-0">
      <div class="tw:text-center tw:sm:text-left tw:mb-4 tw:sm:mb-6">
        <EdroneHeader
          :title="translation.title"
          :desc="translation.subtitle"
          :show-progress="true"
          :current-step="2"
          :total-steps="4"
        />
      </div>

      <div class="tw:space-y-3">
        <EdroneInput
          :label="translation.name.label"
          :placeholder="translation.name.placeholder"
          :value="name"
          :invalid="!!formValidationErrors.name"
          :error-msg="formValidationErrors.name"
          autocomplete="given-name"
          name="firstName"
          @input="(value) => handleChange(value, 'name')"
          @blur="validateForm('name')"
        />
        <EdroneInput
          :label="translation.email.label"
          :placeholder="translation.email.placeholder"
          :value="email"
          :invalid="!!formValidationErrorsBackend.email || !!formValidationErrors.email"
          :error-msg="formValidationErrorsBackend.email || formValidationErrors.email"
          autocomplete="email"
          name="email"
          @input="(value) => handleChange(value, 'email')"
          @blur="
            () => {
              validateForm('email');
              setShopUrlByEmail();
            }
          "
        />
        <EdroneInput
          :label="translation.phone.label"
          :placeholder="translation.phone.placeholder"
          :value="phoneNumber"
          :invalid="!!formValidationErrors.phoneNumber"
          :error-msg="formValidationErrors.phoneNumber"
          autocomplete="tel"
          name="phoneNumber"
          @input="(value) => handleChange(value, 'phoneNumber')"
          @blur="validateForm('phoneNumber')"
        />
        <EdroneInput
          :label="translation.url.label"
          :placeholder="translation.url.placeholder"
          :value="shopUrl"
          :invalid="!!formValidationErrorsBackend.shopUrl || !!formValidationErrors.shopUrl"
          :error-msg="formValidationErrorsBackend.shopUrl || formValidationErrors.shopUrl"
          autocomplete="url"
          name="shopUrl"
          @input="(value) => handleChange(value, 'shopUrl')"
          @blur="handleShopUrlBlur"
        />
      </div>

      <RegisterFormCaptcha />

      <!-- Desktop buttons - visible only on larger screens -->
      <div class="tw:hidden tw:md:flex tw:gap-2 tw:mt-10">
        <EdroneButtonIcon unique-id="RegisterForm_goback-button" :disabled="isLoading" @click="goBack">
          <font-awesome-icon icon="fa-light fa-arrow-left" />
        </EdroneButtonIcon>
        <EdroneButton
          class="tw:w-full tw:h-full"
          unique-id="RegisterForm_submit-button"
          :disabled="!isFormValid"
          :loading="isLoading"
          @click="handleSubmit"
        >
          {{ translation.submit }}
        </EdroneButton>
      </div>
      <p class="tw:hidden tw:md:block tw:text-center tw:text-xs tw:text-default-600 tw:mt-2">
        *{{ trans._register.platforms.noCardRequired }}
      </p>

      <p
        v-if="formValidationErrorsBackend.captcha"
        class="tw:hidden tw:md:flex tw:md:justify-center tw:caption tw:text-red-500 tw:mt-2"
      >
        {{ translation.notification.error.register }}
      </p>

      <!-- This solution is safe against XSS because translation.agreements.text and links come from a static YAML file in the repository. -->
      <p class="tw:body-2 tw:mt-6" v-html="agreementsText"></p>
    </div>

    <div
      class="tw:fixed tw:bottom-0 tw:left-0 tw:right-0 tw:bg-white tw:border-t tw:border-default-200 tw:p-4 tw:z-20 tw:shadow-lg tw:md:hidden"
    >
      <div class="tw:max-w-xs tw:sm:max-w-xl tw:mx-auto tw:flex tw:flex-col tw:gap-2">
        <EdroneButton
          class="tw:w-full"
          unique-id="RegisterForm_submit-button-mobile"
          :disabled="!isFormValid"
          :loading="isLoading"
          @click="handleSubmit"
        >
          {{ translation.submit }}
        </EdroneButton>
        <p class="tw:caption tw:text-center tw:text-default-600">*{{ trans._register.platforms.noCardRequired }}</p>
      </div>
      <p v-if="formValidationErrorsBackend.captcha" class="tw:caption tw:text-red-500 tw:text-center tw:mt-2">
        {{ translation.notification.error.register }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from "axios";
import { ref, onMounted } from "vue";
import { storeToRefs } from "pinia";
import { useRegisterStore } from "vue-app/pages/PageRegister/PageRegister.store";
import { useRouter } from "vue-router";
import { splitAtFirstSpace, validateForm } from "./RegisterForm.helpers";
import EdroneButton from "vue-app/edroneComponents/EdroneButton.vue";
import EdroneInput from "vue-app/edroneComponents/EdroneInput.vue";
import EdroneButtonIcon from "edrone-components/EdroneButtonIcon.vue";
import EdroneHeader from "edrone-components/EdroneHeader.vue";
import RegisterFormCaptcha from "./RegisterFormCaptcha.vue";
import { amplitudeSendEvent } from "vue-app/utils/amplitude";

interface Window {
  grecaptcha?: {
    execute?: (id?: string) => void;
    reset?: () => void;
    getResponse?: () => string;
  };
  assignRecaptchaRegister?: () => void;
}

onMounted(() => {
  fetchRestrictedDomains();
  window.scrollTo(0, 0);
});

const translation = window._trans._register.form;

const router = useRouter();

const store = useRegisterStore();
const {
  selectedPlatform,
  isFormValid,
  formValidationErrors,
  formValidationErrorsBackend,
  name,
  phoneNumber,
  email,
  shopUrl,
  captcha,
} = storeToRefs(store);

const isLoading = ref<boolean>(false);
const restrictedDomains = ref<string>("");

const fetchRestrictedDomains = async () => {
  try {
    const { data } = await axios.get(
      "https://dgk28ckagqims.cloudfront.net/mission-control/PageRegister/email_provider_domains.txt",
      {
        headers: {
          "Content-Type": "text/plain",
        },
      }
    );
    restrictedDomains.value = data;
  } catch (e) {
    console.error(e);
  }
};

type FormFieldKey = "name" | "phoneNumber" | "email" | "shopUrl";

const extractStringValue = (value: string | string[]): string => {
  return Array.isArray(value) ? value[0] || "" : value;
};

const updateStoreField = (key: FormFieldKey, value: string): void => {
  switch (key) {
    case "name":
      store.name = value;
      break;
    case "phoneNumber":
      store.phoneNumber = value;
      break;
    case "email":
      store.email = value;
      break;
    case "shopUrl":
      store.shopUrl = value;
      break;
  }
};

const clearBackendValidationError = (key: FormFieldKey): void => {
  if (key === "email") {
    formValidationErrorsBackend.value.email = "";
  }
  if (key === "shopUrl") {
    formValidationErrorsBackend.value.shopUrl = "";
  }
};

const handleChange = (value: string | string[], key: FormFieldKey): void => {
  const stringValue = extractStringValue(value);
  updateStoreField(key, stringValue);
  clearBackendValidationError(key);
};

const setShopUrlByEmail = () => {
  const userDomain = email.value.split("@")[1] ?? "";
  if (userDomain && !shopUrl.value && !restrictedDomains.value.includes(userDomain)) {
    shopUrl.value = `https://${userDomain}`;
  }
};

const handleShopUrlBlur = () => {
  if (shopUrl.value && !shopUrl.value.startsWith("http://") && !shopUrl.value.startsWith("https://")) {
    store.shopUrl = `https://${shopUrl.value}`;
  }
  validateForm("shopUrl");
};

const agreementsText = translation.agreements.text
  .replace(
    /\{Terms of Service\}/g,
    `<a href="${translation.agreements.links.termsOfService}" target="_blank" class="tw:text-blue-500">${translation.agreements.termsOfService}</a>`
  )
  .replace(
    /\{Privacy Policy\}/g,
    `<a href="${translation.agreements.links.privacyPolicy}" target="_blank" class="tw:text-blue-500">${translation.agreements.privacyPolicy}</a>`
  );

const handleSubmit = async () => {
  validateForm();
  if (isFormValid.value) {
    // Trigger reCAPTCHA: reset and execute the widget.
    // This will asynchronously call the global callback assignRecaptchaRegister once the user is verified and a token is available.
    const grecaptcha = (window as Window).grecaptcha;
    const recaptchaDiv = document.getElementById("g-recaptcha");
    if (
      grecaptcha &&
      typeof grecaptcha.execute === "function" &&
      typeof grecaptcha.reset === "function" &&
      recaptchaDiv
    ) {
      grecaptcha.reset();
      grecaptcha.execute();
    }
  } else {
    amplitudeSendEvent("register_form_validation_failed", {
      platform: String(selectedPlatform.value),
      step: "form",
      name_error: formValidationErrors.value.name || "",
      email_error: formValidationErrors.value.email || "",
      phone_error: formValidationErrors.value.phoneNumber || "",
      url_error: formValidationErrors.value.shopUrl || "",
      captcha_error: formValidationErrors.value.captcha || "",
    });
  }
};

// assignRecaptchaRegister is a global callback set for the invisible reCAPTCHA widget.
// It is called automatically by reCAPTCHA after the user is verified (or determined to be a bot).
// Only here do we have a valid captcha token, so only now can we safely call register().
// This ensures register() is never called without a valid, fresh token.
(window as Window).assignRecaptchaRegister = async function () {
  // Get the captcha token from the widget and store it
  const grecaptcha = (window as Window).grecaptcha;
  if (grecaptcha && typeof grecaptcha.getResponse === "function") {
    const token = grecaptcha.getResponse() || "";
    captcha.value = token;
  }
  // proceed with registration with a valid token
  await register();
  // clear the token after use for security purposes
  captcha.value = "";
};

const goBack = () => {
  amplitudeSendEvent("register_form_back", {
    platform: String(selectedPlatform.value),
    step: "form",
    name_filled: !!name.value,
    email_filled: !!email.value,
    phone_filled: !!phoneNumber.value,
    url_filled: !!shopUrl.value,
  });
  router.push("/");
};

async function register() {
  isLoading.value = true;

  amplitudeSendEvent("register_api_request_start", {
    platform: String(selectedPlatform.value),
    step: "form",
  });

  try {
    const { data } = await axios.post("/register", {
      first_name: splitAtFirstSpace(name.value)[0],
      last_name: splitAtFirstSpace(name.value)[1] || "",
      email: email.value,
      subscribe: true,
      phone: phoneNumber.value,
      url: shopUrl.value,
      code: new URL(location.href).searchParams.get("code"),
      onboarding_id: "",
      platform: selectedPlatform.value,
      g_recaptcha_response: captcha.value,
      lang: new URL(location.href).searchParams.get("lang"),
      confirmed: new URL(location.href).searchParams.get("confirmed"),
    });

    if (data.status === "success") {
      amplitudeSendEvent("register_success", {
        platform: String(selectedPlatform.value),
        email_domain: email.value.split("@")[1],
        has_referral_code: !!new URL(location.href).searchParams.get("code"),
        language: new URL(location.href).searchParams.get("lang") || "en",
      });
      window.location.href = "/login/trackers";
    } else {
      let errorType = "unknown";
      let errorMessage = "";

      if (data.error._url === "URL already exists") {
        errorType = "url_exists";
        errorMessage = translation.validation.backend.urlExists;
        formValidationErrorsBackend.value.shopUrl = translation.validation.backend.urlExists;
      }
      if (data.error._email === "Email already exists") {
        errorType = "email_exists";
        errorMessage = translation.validation.backend.emailExists;
        formValidationErrorsBackend.value.email = translation.validation.backend.emailExists;
      }

      if (data.error._email === "Email is invalid") {
        errorType = "email_invalid";
        errorMessage = translation.validation.backend.emailIsInvalid;
        formValidationErrorsBackend.value.email = translation.validation.backend.emailIsInvalid;
      }

      if (data.error._captcha === "Captcha not valid") {
        errorType = "captcha_invalid";
        errorMessage = translation.validation.backend.captchaInvalid;
        formValidationErrorsBackend.value.captcha = translation.validation.backend.captchaInvalid;
        const grecaptcha = (window as Window).grecaptcha;
        if (typeof grecaptcha === "object" && typeof grecaptcha.reset === "function") {
          grecaptcha.reset();
        }
      }

      amplitudeSendEvent("register_form_api_error", {
        platform: String(selectedPlatform.value),
        error_type: errorType,
        error_message: errorMessage,
        step: "form",
      });
    }
  } catch (error) {
    amplitudeSendEvent("register_form_api_error", {
      platform: String(selectedPlatform.value),
      error_type: "network_error",
      error_message: "Network error or server error",
      step: "form",
    });
  } finally {
    isLoading.value = false;
  }
}
</script>

<style>
@supports (-webkit-touch-callout: none) {
  @media screen and (max-device-width: 812px) and (orientation: portrait) {
    input,
    select,
    textarea {
      font-size: 16px !important;
    }
  }
}
</style>
